import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, Users, Clock, Check, EyeOff, Trash2, X, Play, FileText, Mic, Video, Mail, Lock, Crown, Heart, FileDown, BookOpen, AlertTriangle, RotateCcw, CalendarPlus } from "lucide-react";
import api, { fileUrl, downloadFile } from "@/lib/api";
import { getTheme, themeStyle, eventTitle } from "@/lib/eventTheme";
import { StatusChip } from "@/components/Common";
import { PlanBadge, UpgradeDialog } from "@/components/Upgrade";
import InvitationsPanel from "@/components/InvitationsPanel";
import CompilationPanel from "@/components/CompilationPanel";
import TeamPanel from "@/components/TeamPanel";
import { useAuth } from "@/context/AuthContext";

function daysLeft(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return d;
}

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ev, setEv] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("tributes");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([api.get(`/events/${eventId}`), api.get(`/events/${eventId}/submissions`)]);
      setEv(e.data); setSubs(s.data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/login");
      else toast.error("Could not load tribute");
    } finally { setLoading(false); }
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Intercept ?extend=7 (from zero-submissions organizer email) — open the extend modal preselected.
  useEffect(() => {
    const q = searchParams.get("extend");
    if (q) {
      const d = Math.max(1, Math.min(90, parseInt(q, 10) || 7));
      setExtendDays(d);
      setExtendOpen(true);
      setSearchParams((p) => { p.delete("extend"); return p; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (loading || !ev) return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" /></div>;

  const theme = getTheme(ev.type);
  const url = `${window.location.origin}/t/${ev.slug}`;
  const dl = daysLeft(ev.settings?.deadline);
  const filtered = subs.filter((s) => filter === "all" ? true : s.status === filter);

  const moderate = async (sub, status) => {
    try { await api.patch(`/events/${eventId}/submissions/${sub.submission_id}`, { status }); await load(); if (active) setActive({ ...active, status }); toast.success(status === "approved" ? "Approved" : "Hidden"); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (sub) => {
    try { await api.delete(`/events/${eventId}/submissions/${sub.submission_id}`); setActive(null); await load(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };
  const closeEarly = async () => {
    try { await api.patch(`/events/${eventId}`, { status: ev.status === "collecting" ? "compiling" : "collecting" }); await load(); toast.success(ev.status === "collecting" ? "Submissions closed" : "Submissions reopened"); }
    catch { toast.error("Could not update"); }
  };
  const saveDeadline = async (val) => {
    try { const iso = val ? new Date(val).toISOString() : null; await api.patch(`/events/${eventId}`, { deadline: iso }); await load(); toast.success("Deadline updated"); }
    catch { toast.error("Could not update deadline"); }
  };

  const extendDeadline = async (days) => {
    try {
      const r = await api.post(`/events/${eventId}/extend-deadline`, { days: Number(days) || 7 });
      await load();
      setExtendOpen(false);
      const when = new Date(r.data.deadline).toLocaleString();
      toast.success(`Extended by ${days} days — new deadline ${when}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend the deadline");
    }
  };

  const restore = async (sub) => {
    try { await api.post(`/events/${eventId}/submissions/${sub.submission_id}/restore`); setActive(null); await load(); toast.success("Restored to pending — review to approve"); }
    catch { toast.error("Could not restore"); }
  };

  const deleteEvent = async () => {
    setDeleting(true);
    try {
      await api.delete(`/events/${eventId}`, { data: { confirm_name: confirmName } });
      toast.success("Event and all data deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally { setDeleting(false); }
  };

  const paid = !!ev.entitlements?.paid;
  const exportOrUpsell = async (kind) => {
    if (!paid) { setUpgradeOpen(true); return; }
    try {
      if (kind === "csv") await downloadFile(`/events/${eventId}/contributors.csv`, `gathervoices-${ev.slug}-contributors.csv`);
      else await downloadFile(`/events/${eventId}/guestbook.pdf`, `gathervoices-${ev.slug}-guestbook.pdf`);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="min-h-screen" style={themeStyle(ev.type)}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={16} /> All tributes</button>

        {/* Header */}
        <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] overflow-hidden" style={{ background: `hsl(${theme.accentSoft})` }}>
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
            {ev.photos?.[0] ? <img src={fileUrl(ev.photos[0])} alt="" className="h-24 w-20 rounded-xl object-cover shadow" /> :
              <div className="h-24 w-20 rounded-xl grid place-items-center bg-white/60"><theme.icon style={{ color: `hsl(${theme.accent})` }} /></div>}
            <div className="flex-1">
              <div className="flex items-center gap-3"><StatusChip status={ev.status} /><PlanBadge tier={ev.entitlements?.tier} />{ev.has_pin && <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]"><Lock size={12} /> PIN protected</span>}</div>
              <h1 className="mt-2 font-display text-3xl font-semibold" style={{ color: `hsl(${theme.accentInk})` }}>{eventTitle(ev.type, ev.honoree_name)}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm" style={{ color: `hsl(${theme.accentInk})` }}>
                <span className="inline-flex items-center gap-1.5"><Users size={15} /> <strong data-testid="collected-count">{ev.submission_counts?.total || 0}</strong>{ev.entitlements?.max_tributes ? ` / ${ev.entitlements.max_tributes}` : ""} tributes collected</span>
                {dl !== null && <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {dl < 0 ? "Deadline passed" : dl === 0 ? "Closes today" : `${dl} day${dl === 1 ? "" : "s"} left`}</span>}
              </div>
            </div>
          </div>
          {/* actions */}
          <div className="bg-[hsl(var(--card))] border-t border-[hsl(var(--border))] p-4 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-1 pl-3 flex-1 min-w-[220px]">
              <span className="font-mono text-xs sm:text-sm truncate flex-1">{url}</span>
              <button data-testid="event-link-copy-button" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} className="px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold inline-flex items-center gap-1.5"><Copy size={14} /> Copy</button>
            </div>
            <button data-testid="download-qr-button" onClick={() => downloadQR(ev.slug)} className="px-3 py-2.5 rounded-xl border border-[hsl(var(--border))] text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[hsl(var(--muted))] transition-colors"><Download size={15} /> QR</button>
            <button data-testid="close-early-button" onClick={closeEarly} className="px-3 py-2.5 rounded-xl border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))] transition-colors">{ev.status === "collecting" ? "Close submissions" : "Reopen"}</button>
            {!ev.entitlements?.paid && (
              <button data-testid="header-upgrade-button" onClick={() => setUpgradeOpen(true)} className="px-3 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"><Crown size={15} /> Keep forever</button>
            )}
            <button data-testid="export-csv-button" onClick={() => exportOrUpsell("csv")} className="px-3 py-2.5 rounded-xl border border-[hsl(var(--border))] text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[hsl(var(--muted))] transition-colors" title={paid ? "Export contributors CSV" : "Paid feature"}><FileDown size={15} /> CSV{!paid && <Crown size={12} className="text-[hsl(var(--accent))]" />}</button>
            <button data-testid="guestbook-pdf-button" onClick={() => exportOrUpsell("pdf")} className="px-3 py-2.5 rounded-xl border border-[hsl(var(--border))] text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[hsl(var(--muted))] transition-colors" title={paid ? "Download guest book PDF" : "Paid feature"}><BookOpen size={15} /> Guest book{!paid && <Crown size={12} className="text-[hsl(var(--accent))]" />}</button>
            <QRCodeCanvas id="tribute-qr-canvas" value={url} size={512} includeMargin className="hidden" />
          </div>
        </div>
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} eventId={eventId} />

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-1 border-b border-[hsl(var(--border))]">
          {[["tributes", "Tributes"], ["invitations", "Invitations"], ["film", "Tribute Film"], ["reactions", "Reactions"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className="relative px-4 py-3 text-sm font-semibold transition-colors"
              style={{ color: tab === k ? `hsl(${theme.accentInk})` : "hsl(var(--muted-foreground))" }}>
              {label}
              {tab === k && <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full" style={{ background: `hsl(${theme.accent})` }} />}
            </button>
          ))}
        </div>

        {tab === "invitations" ? (
          <div className="mt-6"><InvitationsPanel ev={ev} /></div>
        ) : tab === "film" ? (
          <div className="mt-6"><CompilationPanel ev={ev} onRefresh={load} /></div>
        ) : tab === "reactions" ? (
          <div className="mt-6"><ReactionsPanel eventId={eventId} theme={theme} /></div>
        ) : (
        <>
        {/* Deadline editor */}
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <label className="font-medium">Deadline:</label>
          <input data-testid="edit-deadline-input" type="datetime-local"
            defaultValue={ev.settings?.deadline ? new Date(ev.settings.deadline).toISOString().slice(0, 16) : ""}
            onChange={(e) => saveDeadline(e.target.value)}
            className="h-10 rounded-xl px-3 bg-[hsl(var(--card))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          <button
            data-testid="extend-deadline-button"
            onClick={() => { setExtendDays(7); setExtendOpen(true); }}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors"
          >
            <CalendarPlus size={15} /> Extend deadline
          </button>
        </div>

        {/* Filter */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {["all", "pending", "approved", "hidden", "superseded"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className="px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors"
              style={{ background: filter === f ? `hsl(${theme.accent})` : "hsl(var(--muted))", color: filter === f ? "#fff" : "hsl(var(--muted-foreground))" }}>
              {f === "superseded" ? "Replaced" : f}{f !== "all" && ev.submission_counts ? ` (${ev.submission_counts[f] || 0})` : ""}
            </button>
          ))}
        </div>

        {/* Gallery */}
        {filtered.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
            <p className="font-display text-lg font-semibold" style={{ color: `hsl(${theme.accentInk})` }}>
              {filter === "superseded" ? "No replaced tributes yet" : "No tributes here yet"}
            </p>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
              {filter === "superseded"
                ? "When someone submits a second time, their older tribute is moved here so you can restore it if you'd like."
                : "Share your link to start collecting — every voice adds to the story."}
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map((s) => (
              <button key={s.submission_id} data-testid={`submission-card-${s.submission_id}`} onClick={() => setActive(s)}
                className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:shadow-[0_12px_30px_rgba(20,16,12,0.10)] transition-shadow">
                <div className="aspect-[4/5] relative grid place-items-center" style={{ background: `hsl(${theme.accentSoft})` }}>
                  {s.kind === "video" && <><video src={fileUrl(s.file_id)} className="absolute inset-0 h-full w-full object-cover" muted preload="metadata" /><div className="relative h-10 w-10 grid place-items-center rounded-full bg-black/50 text-white"><Play size={18} /></div></>}
                  {s.kind === "audio" && <div className="grid place-items-center text-center"><div className="h-12 w-12 grid place-items-center rounded-full" style={{ background: `hsl(${theme.accent})`, color: "#fff" }}><Mic size={22} /></div></div>}
                  {s.kind === "text" && <div className="p-3 text-xs line-clamp-6" style={{ color: `hsl(${theme.accentInk})` }}><FileText size={16} className="mb-1.5" />{s.message}</div>}
                  <span className="absolute top-2 right-2"><StatusChip status={s.status} /></span>
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm truncate">{s.contributor_name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{s.relationship || (s.kind === "text" ? "Written" : s.kind)}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Team seats */}
        <TeamPanel
          eventId={eventId}
          isOwner={ev.my_role === "owner"}
          ownerPlan={user?.plan}
          theme={theme}
          onOpenUpgrade={() => setUpgradeOpen(true)}
        />

        {/* Danger zone — owner only */}
        {ev.my_role === "owner" && (
        <div className="mt-8 mb-2 rounded-2xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--card))] p-5" data-testid="danger-zone">
          <div className="flex items-center gap-2 text-[hsl(var(--destructive))]"><AlertTriangle size={16} /><span className="font-semibold text-sm">Danger zone</span></div>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Delete this event and everything attached to it — every tribute, invitation, reaction, and film. This can&apos;t be undone.
          </p>
          <button
            data-testid="delete-event-open-button"
            onClick={() => { setConfirmName(""); setDangerOpen(true); }}
            className="mt-3 inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))] text-sm font-semibold hover:bg-[hsl(var(--destructive))]/10 transition-colors"
          >
            <Trash2 size={15} /> Delete event &amp; all data
          </button>
        </div>
        )}
        </>
        )}
      </div>

      {/* Submission modal */}
      {active && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50" onClick={() => setActive(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-[hsl(var(--card))] overflow-hidden ever-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--border))]">
              <div><div className="font-semibold">{active.contributor_name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{active.relationship}</div></div>
              <button onClick={() => setActive(null)} className="h-9 w-9 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))]"><X size={18} /></button>
            </div>
            <div className="p-4">
              {active.kind === "video" && <video data-testid="submission-video" src={fileUrl(active.file_id)} controls playsInline className="w-full rounded-xl bg-black" />}
              {active.kind === "audio" && <audio data-testid="submission-audio" src={fileUrl(active.file_id)} controls className="w-full" />}
              {active.kind === "text" && <div><p className="whitespace-pre-wrap leading-relaxed">{active.message}</p>{active.photo_file_id && <img src={fileUrl(active.photo_file_id)} alt="" className="mt-4 rounded-xl w-full object-cover" />}</div>}
            </div>
            <div className="p-4 border-t border-[hsl(var(--border))] flex items-center gap-2">
              {active.status === "superseded" ? (
                <button data-testid="restore-submission-button" onClick={() => restore(active)} className="flex-1 h-11 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold inline-flex items-center justify-center gap-1.5"><RotateCcw size={16} /> Restore this tribute</button>
              ) : (
                <>
                  {active.status !== "approved" && <button data-testid="moderation-approve-button" onClick={() => moderate(active, "approved")} className="flex-1 h-11 rounded-xl bg-[hsl(var(--success))] text-white font-semibold inline-flex items-center justify-center gap-1.5"><Check size={16} /> Approve</button>}
                  {active.status !== "hidden" && <button data-testid="moderation-hide-button" onClick={() => moderate(active, "hidden")} className="flex-1 h-11 rounded-xl border border-[hsl(var(--border))] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[hsl(var(--muted))]"><EyeOff size={16} /> Hide</button>}
                </>
              )}
              <button data-testid="moderation-delete-button" onClick={() => remove(active)} className="h-11 w-11 grid place-items-center rounded-xl border border-[hsl(var(--border))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--muted))]"><Trash2 size={16} /></button>
            </div>
          </div>
        </div>
      )}
      {/* Extend deadline modal */}
      {extendOpen && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/50" onClick={() => setExtendOpen(false)} data-testid="extend-deadline-modal">
          <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] p-6 ever-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}><CalendarPlus size={18} /></div>
              <div>
                <h3 className="font-display text-lg font-semibold">Give people a little more time</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Extend from the current deadline (or from now if it&apos;s already passed).</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-4 gap-2" data-testid="extend-preset-row">
              {[3, 7, 14, 30].map((d) => (
                <button key={d} data-testid={`extend-preset-${d}`} onClick={() => setExtendDays(d)}
                  className={`h-12 rounded-xl font-semibold border transition-colors ${extendDays === d ? "text-white" : "hover:bg-[hsl(var(--muted))]"}`}
                  style={extendDays === d ? { background: `hsl(${theme.accent})`, borderColor: `hsl(${theme.accent})` } : { borderColor: "hsl(var(--border))" }}>
                  {d}d
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">Custom:</span>
              <input data-testid="extend-days-input" type="number" min={1} max={90} value={extendDays} onChange={(e) => setExtendDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                className="h-10 w-24 rounded-xl px-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              <span className="text-[hsl(var(--muted-foreground))]">days</span>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setExtendOpen(false)} className="flex-1 h-11 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]">Not now</button>
              <button data-testid="extend-confirm-button" onClick={() => extendDeadline(extendDays)} className="flex-1 h-11 rounded-xl text-white font-semibold" style={{ background: `hsl(${theme.accent})` }}>Extend by {extendDays}d</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete event modal */}
      {dangerOpen && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/50" onClick={() => !deleting && setDangerOpen(false)} data-testid="delete-event-modal">
          <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] p-6 ever-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-[hsl(var(--destructive))]"><AlertTriangle size={18} /><h3 className="font-display text-lg font-semibold">Delete this event forever?</h3></div>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              This permanently removes {ev.honoree_name}&apos;s tribute page and every tribute, invitation, reaction, and compiled film. This cannot be undone.
            </p>
            <label className="block mt-4 text-sm font-medium">Type <strong>{ev.honoree_name}</strong> to confirm</label>
            <input data-testid="delete-confirm-input" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={ev.honoree_name} autoFocus
              className="mt-1.5 w-full h-11 rounded-xl px-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--destructive))]" />
            <div className="mt-5 flex gap-2">
              <button onClick={() => setDangerOpen(false)} disabled={deleting} className="flex-1 h-11 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] disabled:opacity-60">Keep it</button>
              <button data-testid="delete-confirm-button" disabled={deleting || confirmName.trim().toLowerCase() !== ev.honoree_name.trim().toLowerCase()} onClick={deleteEvent}
                className="flex-1 h-11 rounded-xl bg-[hsl(var(--destructive))] text-white font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
                <Trash2 size={16} /> {deleting ? "Deleting…" : "Delete everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function downloadQR(slug) {
  const canvas = document.getElementById("tribute-qr-canvas");
  if (!canvas) return;
  const a = document.createElement("a");
  a.download = `gathervoices-${slug}.png`;
  a.href = canvas.toDataURL("image/png");
  a.click();
}

function ReactionsPanel({ eventId, theme }) {
  const [data, setData] = useState({ hearts: 0, notes: [], pending: 0 });
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.get(`/events/${eventId}/reactions`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const moderate = async (rid, status) => {
    try { await api.patch(`/events/${eventId}/reactions/${rid}`, { status }); toast.success(status === "approved" ? "Note approved" : "Note hidden"); load(); }
    catch { toast.error("Could not update note"); }
  };

  const pending = data.notes.filter((n) => n.status === "pending");
  const approved = data.notes.filter((n) => n.status === "approved");

  return (
    <div className="space-y-6" data-testid="reactions-panel">
      <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <div className="h-11 w-11 grid place-items-center rounded-xl" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}><Heart size={20} className="fill-current" /></div>
        <div><div className="font-display text-2xl font-semibold" data-testid="reactions-heart-count">{data.hearts}</div><div className="text-sm text-[hsl(var(--muted-foreground))]">hearts from the family</div></div>
      </div>

      <div>
        <h3 className="font-display text-lg font-semibold">Notes awaiting your approval {pending.length > 0 && <span className="text-sm font-normal text-[hsl(var(--muted-foreground))]">({pending.length})</span>}</h3>
        {loading ? <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Loading…</p> :
          pending.length === 0 ? <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">No notes waiting. New notes from the watch page appear here for you to approve.</p> : (
          <div className="mt-3 space-y-3">
            {pending.map((n) => (
              <div key={n.reaction_id} data-testid={`pending-note-${n.reaction_id}`} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
                <p className="leading-relaxed text-sm">{n.message}</p>
                <div className="mt-1 text-sm font-semibold" style={{ color: `hsl(${theme.accent})` }}>{n.name}</div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`approve-note-${n.reaction_id}`} onClick={() => moderate(n.reaction_id, "approved")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold" style={{ background: `hsl(${theme.accent})` }}><Check size={15} /> Approve</button>
                  <button data-testid={`reject-note-${n.reaction_id}`} onClick={() => moderate(n.reaction_id, "rejected")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))]"><X size={15} /> Hide</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {approved.length > 0 && (
        <div>
          <h3 className="font-display text-lg font-semibold">Published notes</h3>
          <div className="mt-3 space-y-2">
            {approved.map((n) => (
              <div key={n.reaction_id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 flex items-start justify-between gap-3">
                <div><p className="text-sm">{n.message}</p><div className="mt-1 text-sm font-semibold" style={{ color: `hsl(${theme.accent})` }}>{n.name}</div></div>
                <button data-testid={`hide-note-${n.reaction_id}`} onClick={() => moderate(n.reaction_id, "rejected")} className="shrink-0 h-9 w-9 grid place-items-center rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]" title="Hide"><EyeOff size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
